import CollapsibleView from "@components/molecules/CollapsibleView";
import { PersonalizationProps } from "./types";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { dynamicMapToRowType, generateYupSchema, onChangeValue } from "./utils";
import { useEnablePersonalisationFont } from "@redux/slices/globalSlice";
import { useTranslation } from "react-i18next";
import { Input, VDBText } from "@components/atoms";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { RowType } from "@components/molecules/DynamicForm/types";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import CustomSelect from "@components/atoms/CustomSelect/CustomSelect";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useJewelryConfiguratorState } from "../Context/JewelryConfiguratorContext";
import { DeviceEventEmitter } from "react-native";
import { View } from "react-native";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import useEngraveFonts from "../../../../hooks/useEngraveFonts";

const Personalization = ({
  title,
  sub_options,
  engravable,
  disabled = false,
  onMultilineFocusChange,
}: PersonalizationProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const enablePersonalisationFont = useEnablePersonalisationFont();
  const cursorPosition = useRef(0);
  const engraveTextInputRef = useRef(null);
  const inputRefs = useRef<Record<string, any>>({});
  const isFocusMovingRef = useRef(false);
  const { setPersonalization, personalization, setHasPersonalizationError } =
    useJewelryConfiguratorState();
  const requiredSymbol = "*";

  let filteredFields = Object.values(sub_options)
    .filter((field) => field.price_modes.includes(userInfo.getUserPriceMode()))
    .sort((a, b) => a.order - b.order)
    .map((field) => dynamicMapToRowType<RowType>(field) as RowType);

  if (!engravable) {
    filteredFields = filteredFields.filter((field) => !field.is_Engravable);
  }

  const textInputFields = useMemo(
    () =>
      filteredFields.filter(
        (field) => field.type !== "dropdown" && field.type !== "grid"
      ),
    [filteredFields]
  );

  const focusNextField = useCallback(
    (currentName: string) => {
      const index = textInputFields.findIndex((f) => f.name === currentName);
      const nextField = textInputFields[index + 1];
      if (nextField && inputRefs.current[nextField.name]) {
        isFocusMovingRef.current = true;
        setTimeout(() => {
          inputRefs.current[nextField.name]?.focus();
        }, 50);
        setTimeout(() => {
          isFocusMovingRef.current = false;
        }, 200);
      }
    },
    [textInputFields]
  );

  const focusPrevField = useCallback(
    (currentName: string) => {
      const index = textInputFields.findIndex((f) => f.name === currentName);
      const prevField = textInputFields[index - 1];
      if (prevField && inputRefs.current[prevField.name]) {
        isFocusMovingRef.current = true;
        setTimeout(() => {
          inputRefs.current[prevField.name]?.focus();
        }, 50);
        setTimeout(() => {
          isFocusMovingRef.current = false;
        }, 200);
      }
    },
    [textInputFields]
  );

  const schema = generateYupSchema(filteredFields, t);

  useEffect(() => {
    Object.entries(personalization ?? []).forEach(([key, value]) => {
      setValue(key, value);
    });
  }, [personalization]);

  const {
    control,
    formState: { errors },
    setError,
    getValues,
    clearErrors,
    setValue,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchedEngraveFont = watch("engrave_font");

  const { getFontFamilyForStyle, loadFont } = useEngraveFonts();

  useEffect(() => {
    if (enablePersonalisationFont && watchedEngraveFont) {
      loadFont(watchedEngraveFont);
    }
  }, [watchedEngraveFont, loadFont, enablePersonalisationFont]);

  // Only apply font styling when the feature switch is enabled
  const engraveFontFamily =
    enablePersonalisationFont && watchedEngraveFont
      ? getFontFamilyForStyle(watchedEngraveFont)
      : undefined;

  const setPersonalizationValues = (values: any) => {
    setPersonalization(values);
  };

  const addSymbol = (symbolName: string) => {
    let symbol = "";
    switch (symbolName) {
      case "heart":
        symbol = "♡";
        break;
      case "two_hearts":
        symbol = "💕";
        break;
      case "infinity":
        symbol = "∞";
        break;
      default:
        symbol = "";
        break;
    }
    const currentText = getValues("engrave_text") || "";
    const newText =
      currentText.slice(0, cursorPosition.current) +
      symbol +
      currentText.slice(cursorPosition.current);
    const engraveTextField = filteredFields.find(
      (field) => field.name === "engrave_text"
    );
    const maxLength =
      engraveTextField?.validations?.maxLength ||
      engraveTextField?.characterLimit;

    if (maxLength && newText.length > maxLength) {
      return;
    }

    setValue("engrave_text", newText);
    const newCursorPosition = cursorPosition.current + symbol.length;
    cursorPosition.current = newCursorPosition;

    setTimeout(() => {
      if (engraveTextInputRef.current) {
        engraveTextInputRef.current.setSelection(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);

    clearErrors("engrave_text");
    trigger("engrave_text");

    setErrors(getValues());
    setPersonalizationValues(getValues());
  };

  const setErrors = useCallback(
    (values: any) => {
      const engraveText = values.engrave_text;
      const engraveFont = values.engrave_font;

      const hasTextButNoFont =
        engraveText &&
        engraveText.trim().length > 0 &&
        (!engraveFont || engraveFont.trim().length === 0);

      const engraveTextField = filteredFields.find(
        (field) => field.name === "engrave_text"
      );
      const engraveFontField = filteredFields.find(
        (field) => field.name === "engrave_font"
      );
      const isEngraveTextRequired = engraveTextField?.validations?.required;

      const hasFontButNoText =
        engraveFont &&
        engraveFont.trim().length > 0 &&
        (!engraveText || engraveText.trim().length === 0) &&
        isEngraveTextRequired;

      if (hasTextButNoFont && engraveFontField) {
        setError("engrave_font", {
          type: "manual",
          message: t(engraveFontField.validations?.errorMessage ?? ""),
        });
      } else {
        clearErrors("engrave_font");
      }

      if (hasFontButNoText && engraveTextField) {
        setError("engrave_text", {
          type: "manual",
          message: t(engraveTextField.validations?.errorMessage ?? ""),
        });
      } else {
        clearErrors("engrave_text");
      }

      setHasPersonalizationError(hasTextButNoFont || hasFontButNoText);
    },
    [filteredFields, setError, clearErrors, t, setHasPersonalizationError]
  );

  const renderField = (field: RowType) => {
    const commonProps = {
      control,
      name: field.name,
      render: ({ field: { onChange, value } }) => {
        const label = `${t(field.label)}${
          field?.validations?.required ? requiredSymbol : ""
        }`;

        const isTextInputField =
          field.type !== "dropdown" && field.type !== "grid";
        const isLastField =
          textInputFields[textInputFields.length - 1]?.name === field.name;
        const isFirstField = textInputFields[0]?.name === field.name;
        const registerInputRef = (ref: any) => {
          inputRefs.current[field.name] = ref;
          if (field.name === "engrave_text") {
            engraveTextInputRef.current = ref;
          }
        };
        const error = errors?.[field.name]?.message as string;

        switch (field?.type) {
          case "dropdown":
            const isEngraveFontField = field.name === "engrave_font";
            return (
              <>
                <CustomSelect
                  title={t(label)}
                  defaultValue={value}
                  options={field.options ?? []}
                  onSelect={async (_value) => {
                    await onChangeValue(field, _value, onChange);
                    setErrors(getValues());
                    if (field.name === "engrave_font") {
                      const engraveText = getValues("engrave_text");
                      if (engraveText && engraveText.trim().length > 0) {
                        trigger("engrave_text");
                      }
                    }
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  showError={field.name in errors && error.trim() !== ""}
                  errorMessage={error}
                  disabled={disabled}
                  useFontStyle={isEngraveFontField}
                />
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
          case "textarea":
            return (
              <>
                <Input
                  inputLabel={t(label)}
                  inputRef={registerInputRef}
                  returnKeyType={isLastField ? "done" : "next"}
                  blurOnSubmit={false}
                  onSubmitEditing={() => {
                    if (!isLastField) {
                      focusNextField(field.name);
                    }
                  }}
                  showAccessoryPrevNext
                  onAccessoryPrev={
                    !isFirstField
                      ? () => {
                          focusPrevField(field.name);
                        }
                      : undefined
                  }
                  onAccessoryNext={
                    !isLastField
                      ? () => {
                          focusNextField(field.name);
                        }
                      : undefined
                  }
                  onChangeText={(val) => {
                    onChangeValue(field, val, onChange);
                    clearErrors(field.name);
                  }}
                  value={value}
                  keyboardType="default"
                  height="96"
                  multiline
                  error={error}
                  onFocus={() => {
                    onMultilineFocusChange?.(true);
                  }}
                  onBlur={() => {
                    if (isFocusMovingRef.current) {
                      return;
                    }
                    onMultilineFocusChange?.(false);
                    setErrors(getValues());
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  disabled={disabled}
                />
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
          case "grid":
            return (
              <GridContainer>
                {field && field?.options?.length
                  ? field?.options.map((icon, iconIndex) => (
                      <View key={icon?.value || icon?.name || iconIndex}>
                        <StyledIcon
                          onPress={() =>
                            addSymbol(icon?.value || icon?.name || "heart")
                          }
                        >
                          <VDBIcon
                            name={icon?.icon?.data || VDBIcons.HEART}
                            uri={icon?.icon?.data}
                            size={24}
                            color={theme["core-06"]}
                            variant={
                              icon?.icon?.type === IconsType.img_url
                                ? IconsVariant.CustomUrl
                                : IconsVariant.VDBCustom
                            }
                          />
                        </StyledIcon>
                      </View>
                    ))
                  : null}
              </GridContainer>
            );
          default:
            const isEngraveTextField = field.name === "engrave_text";

            return (
              <>
                <Input
                  inputLabel={t(label)}
                  inputRef={isTextInputField ? registerInputRef : undefined}
                  returnKeyType={isLastField ? "done" : "next"}
                  blurOnSubmit={false}
                  onSubmitEditing={() => {
                    if (!isLastField) {
                      focusNextField(field.name);
                    }
                  }}
                  showAccessoryPrevNext
                  onAccessoryPrev={
                    !isFirstField
                      ? () => {
                          focusPrevField(field.name);
                        }
                      : undefined
                  }
                  onAccessoryNext={
                    !isLastField
                      ? () => {
                          focusNextField(field.name);
                        }
                      : undefined
                  }
                  onChangeText={(val) => {
                    onChangeValue(field, val, onChange);
                    clearErrors(field.name);
                    setPersonalizationValues(getValues());
                  }}
                  onSelectionChange={
                    isEngraveTextField
                      ? (event) => {
                          cursorPosition.current =
                            event.nativeEvent.selection.start;
                        }
                      : undefined
                  }
                  value={value}
                  error={error}
                  onBlur={() => {
                    if (isFocusMovingRef.current) {
                      return;
                    }
                    trigger(field.name);
                    setErrors(getValues());
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  disabled={disabled}
                  {...(isEngraveTextField &&
                    engraveFontFamily && { fontFamily: engraveFontFamily })}
                />
                {field.characterLimit && field.characterLimitText && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.characterLimitText, {
                      limit: field.characterLimit,
                      remainingLimit:
                        Number(field.characterLimit) -
                        (getValues()[field.name]?.length ?? 0),
                    })}
                  </VDBText>
                )}
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
        }
      },
    };

    return <Controller {...commonProps} />;
  };

  return (
    <CollapsibleView
      initExpanded={true}
      title={title}
      arrowStyling={{ color: theme["core-04"] }}
      applyShadow
    >
      <Divider />
      <InputContainer>
        {filteredFields.map((field) => (
          <React.Fragment key={field.name}>{renderField(field)}</React.Fragment>
        ))}
      </InputContainer>
    </CollapsibleView>
  );
};

const GridContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const InputContainer = styled.View<{}>`
  padding: ${SpaceType.Double}px;
  gap: 10px;
`;

const StyledIcon = styled.TouchableOpacity`
  padding: ${SpaceType.LargeDouble}px;
`;

export default Personalization;
